import "./index.css";
import { reloadOnceForStaleChunk } from "@/lib/lazy-with-reload";
import { applySeasonalSiteTheme } from "@/lib/seasonal-site-theme";

applySeasonalSiteTheme();

function installDomMutationCompatibilityGuard() {
  const proto = window.Node?.prototype;
  if (!proto || (proto as any).__gradeAReactDomGuardInstalled) return;

  const originalRemoveChild = proto.removeChild;
  const originalInsertBefore = proto.insertBefore;

  Object.defineProperty(proto, "__gradeAReactDomGuardInstalled", {
    value: true,
    configurable: false,
  });

  proto.removeChild = function<T extends Node>(child: T): T {
    if (child?.parentNode !== this) return child;
    return originalRemoveChild.call(this, child) as T;
  };

  proto.insertBefore = function<T extends Node>(newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

installDomMutationCompatibilityGuard();

/**
 * Global error capture. ErrorBoundary only catches React render errors —
 * async crashes (event handlers, setTimeout, promise rejections, parse
 * errors before React mounts) silently disappear unless we install
 * window.onerror + unhandledrejection here. This is what gives us a stack
 * trace for the next round of "Customer Error Alerts" so we don't have to
 * reverse-engineer the bug from a single-line minified message.
 *
 * Same dedupe story as ErrorBoundary — server-side already throttles by
 * (message, alertSent) within a 30-min window, so flooding the endpoint
 * isn't a concern. Local dedupe avoids re-posting the same exception
 * multiple times within the same page view.
 */
function installGlobalErrorReporter() {
  const recent = new Set<string>();
  const post = (payload: unknown) => {
    try {
      const body = JSON.stringify(payload);
      // sendBeacon is more reliable across page-unload events; fall back to fetch.
      if (navigator.sendBeacon) {
        navigator.sendBeacon("/api/client-error", new Blob([body], { type: "application/json" }));
      } else {
        fetch("/api/client-error", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    } catch {}
  };

  // Third-party CDN scripts (analytics/payments/tag managers) throw errors we
  // can't fix and that aren't our app breaking — most notably Clarity's
  // `a[c].q.unshift` during SPA navigation. Don't flood the error log with them.
  const THIRD_PARTY_SCRIPT_RE = /clarity\.ms|googletagmanager\.com|google-analytics\.com|gtag\/js|connect\.facebook\.net|fbevents|recaptcha|gstatic\.com|squarecdn\.com|js\.stripe\.com|doubleclick|cobrowse|hotjar|q\.unshift/i;

  // Stale-chunk-after-deploy errors. Every deploy renames the hashed chunks, so
  // a page open from before the deploy 404s when it lazy-loads a route — surfacing
  // as "Importing a module script failed" / "Failed to fetch dynamically imported
  // module" / React lazy's "_result.default". lazyWithReload already RECOVERS these
  // by reloading to the fresh build, so they're transient and self-healing — don't
  // report/email them. (They sent alarming "what is this?" emails for a deploy
  // hiccup that already fixed itself. Caught 2026-06-30.)
  // 2026-07-09 — added the "Cannot read properties of undefined (reading
  // 'default')" / "undefined is not an object (evaluating '...default')"
  // phrasing — the same resolved-but-empty-module case error-boundary.tsx
  // and lazy-with-reload.ts now also recognize and recover from.
  const CHUNK_LOAD_RE = /importing a module script failed|failed to fetch dynamically imported module|error loading dynamically imported module|_result\.default|unable to preload css|failed to load module script|dynamically imported module|cannot read propert(?:y|ies) of undefined \(reading 'default'\)|undefined is not an object \(evaluating '.*\.default'\)/i;

  window.addEventListener("error", (event: ErrorEvent) => {
    try {
      const msg = event.message || "Unknown error";
      // ResizeObserver loop is a benign browser timing quirk fired by Radix UI
      // components measuring their own size. It's not a real error — suppress it
      // before React's listener runs so it never triggers the ErrorBoundary.
      if (msg.includes("ResizeObserver loop")) {
        event.stopImmediatePropagation();
        event.preventDefault();
        return;
      }
      const stack = event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`;
      if (THIRD_PARTY_SCRIPT_RE.test(`${event.filename || ""} ${stack} ${msg}`)) return;
      if (CHUNK_LOAD_RE.test(`${msg} ${stack}`)) return; // self-healing stale-chunk reload
      const key = `${msg}|${event.filename || ""}|${event.lineno || 0}`;
      if (recent.has(key)) return;
      recent.add(key);
      post({
        message: msg.slice(0, 500),
        stack: String(stack).slice(0, 2000),
        componentStack: undefined,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        source: "window.onerror",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    } catch {}
  });

  // Square's Web Payments SDK fires a fire-and-forget internal promise for 3DS
  // status. If the customer abandons or times out the bank challenge, that
  // promise rejects with no awaiter and surfaces here. It's a customer-side
  // event (closed popup, slow link, blocked iframe), not a code bug — drop it
  // so it doesn't page us.
  const IGNORED_REJECTION_PATTERNS = [
    /three\s*ds\s*method\s*timed\s*out/i,
    // Square's Cash App Pay SDK fetches a QR PNG from api.cash.app internally.
    // If the image fails (expired grant, network blip) the SDK rejects a
    // fire-and-forget promise we can't catch — not actionable, suppress it.
    /could not load.*api\.cash\.app/i,
    /api\.cash\.app.*could not load/i,
  ];

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    try {
      const reason = event.reason;
      const msg = reason instanceof Error ? reason.message : (typeof reason === "string" ? reason : JSON.stringify(reason).slice(0, 200));
      if (IGNORED_REJECTION_PATTERNS.some((p) => p.test(msg))) return;
      const stack = reason instanceof Error ? reason.stack : "";
      if (CHUNK_LOAD_RE.test(`${msg} ${stack}`)) return; // self-healing stale-chunk reload
      const key = `unhandledrejection|${msg}`;
      if (recent.has(key)) return;
      recent.add(key);
      post({
        message: `Unhandled promise rejection: ${msg}`.slice(0, 500),
        stack: String(stack || "").slice(0, 2000),
        componentStack: undefined,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        source: "unhandledrejection",
      });
    } catch {}
  });
}

installGlobalErrorReporter();

// Vite fires this when a modulepreloaded chunk fails to load — almost always
// because a new deploy replaced the hashed files this open page was built
// against. Reload once to the fresh build instead of dying with "importing a
// module script failed".
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  reloadOnceForStaleChunk();
});

async function mountApp() {
  const [{ createRoot }, { default: App }, React] = await Promise.all([
    import("react-dom/client"),
    import("./App"),
    import("react"),
  ]);
  try {
    if (window.location.pathname === "/") {
      window.localStorage.setItem("gradeAHomeOpened", "1");
    }
  } catch {}
  createRoot(document.getElementById("root")!).render(React.createElement(App));
}

// The loading shell is not interactive content. Start the app immediately
// for every visit; keep theme selection and error recovery installed first.
void mountApp();
